import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { 
  Users, Shield, CheckCircle, ArrowRight, Star, X,
  Brain, TrendingUp, BarChart, Activity, Sparkles,
  Calculator, Award, Smartphone, Key, FileText, AlertTriangle,
  Clock, DollarSign, UserCheck, FileCheck, ClipboardCheck,
  Zap, Target, Check
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AnimatedCounter from '../components/AnimatedCounter';
import SEOHead from '../components/SEOHead';
import { useBooking } from '../context/BookingContext';
import HeroSection from '../components/sections/HeroSection';
import TiredOfSoftwareSection from '../components/sections/TiredOfSoftwareSection';
import YourPlatformSection from '../components/sections/YourPlatformSection';
import CoreModulesSection from '../components/sections/CoreModulesSection';
import ComparisonSection from '../components/sections/ComparisonSection';
import {
  mockMarketStats,
  mockTestimonials,
  mockFeatures,
  mockComparison,
  handleDemoRequest
} from '../mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const { openBooking, openContact } = useBooking();
  const [demoForm, setDemoForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    website: '',
    staffSize: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [testimonials, setTestimonials] = useState(mockTestimonials);
  const [features, setFeatures] = useState(mockFeatures);
  const [heroContent, setHeroContent] = useState(null);

  // Validate Australian phone number
  const validatePhone = (phone) => {
    const cleaned = phone.replace(/[\s\-\(\)]/g, '');
    const mobileRegex = /^(04\d{8}|(\+61|0061)4\d{8})$/;
    const landlineRegex = /^(0[2-9]\d{8}|(\+61|0061)[2-9]\d{8})$/;
    return mobileRegex.test(cleaned) || landlineRegex.test(cleaned);
  };

  const handlePhoneChange = (value) => {
    setDemoForm({ ...demoForm, phone: value });
    if (value && !validatePhone(value)) {
      setPhoneError('Please enter a valid Australian phone number');
    } else {
      setPhoneError('');
    }
  };

  // Fetch CMS content
  useEffect(() => {
    const fetchCMSContent = async () => {
      try {
        // Fetch testimonials from CMS
        const testimonialsRes = await fetch(`${API_URL}/api/cms/testimonials?active_only=true`);
        if (testimonialsRes.ok) {
          const cmsTestimonials = await testimonialsRes.json();
          if (cmsTestimonials.length > 0) {
            // Map CMS 'content' field to 'text' for display compatibility
            const mappedTestimonials = cmsTestimonials.map(t => ({
              ...t,
              text: t.content || t.text
            }));
            setTestimonials(mappedTestimonials);
          }
        }

        // Fetch features from CMS
        const featuresRes = await fetch(`${API_URL}/api/cms/features?active_only=true`);
        if (featuresRes.ok) {
          const cmsFeatures = await featuresRes.json();
          if (cmsFeatures.length > 0) {
            setFeatures(cmsFeatures);
          }
        }

        // Fetch hero content
        const heroRes = await fetch(`${API_URL}/api/cms/hero`);
        if (heroRes.ok) {
          const cmsHero = await heroRes.json();
          setHeroContent(cmsHero);
        }
      } catch (error) {
        console.log('Using fallback content');
      }
    };

    fetchCMSContent();
  }, []);

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone
    if (!demoForm.phone || !validatePhone(demoForm.phone)) {
      setPhoneError('Please enter a valid Australian phone number');
      toast.error('Please enter a valid Australian phone number');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const result = await handleDemoRequest(demoForm);
      toast.success(result.message);
      setDemoForm({ name: '', email: '', company: '', phone: '', website: '', staffSize: '', message: '' });
      setPhoneError('');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const iconMap = {
    Users, UserCheck, FileText, AlertTriangle, DollarSign, Smartphone,
    Brain, Shield, FileCheck, ClipboardCheck
  };

  return (
    <div className="min-h-screen bg-page-bg">
      <SEOHead 
        title="GetCare 360 | NDIS Provider Software Australia"
        description="AI-powered NDIS software platform for Australian disability care providers. Streamline compliance, rostering, incident management & NDIS claims. Own your software forever."
        keywords="NDIS software, NDIS provider software, disability care software, NDIS compliance, NDIS rostering, Australian NDIS, care management software, NDIS claims"
        canonicalUrl="/"
        page="home"
      />
      <Toaster position="top-right" richColors />
      <Header />

      {/* Hero Section - NEW INTERACTIVE */}
      <HeroSection heroContent={heroContent} openBooking={openBooking} openContact={openContact} />

      {/* Trust Section */}
      <section className="py-16 bg-primary-navy relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Key, text: 'Own Your Software', desc: 'No subscriptions, yours forever' },
              { icon: Smartphone, text: 'Mobile App Included', desc: 'iOS & Android for your team' },
              { icon: Brain, text: 'AI-Powered', desc: 'Compliance automation built-in' },
              { icon: Award, text: 'Australian Team', desc: 'Local support & hosting' }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div 
                  key={index} 
                  className="bg-white/5 border border-white/10 p-6 rounded-2xl text-white text-center group hover:bg-white/10 hover:border-care-teal/50 hover:-translate-y-1 hover:shadow-lg hover:shadow-care-teal/10 transition-all duration-300 cursor-default"
                >
                  <div className="w-16 h-16 rounded-2xl bg-care-teal/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-care-teal/20 group-hover:scale-110 transition-all duration-300">
                    <Icon className="text-care-teal" size={28} />
                  </div>
                  <div className="font-semibold text-lg mb-1 group-hover:text-care-teal transition-colors">{item.text}</div>
                  <div className="text-white/70 text-sm group-hover:text-white/90 transition-colors">{item.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* The Problem Section - NEW INTERACTIVE */}
      <TiredOfSoftwareSection openBooking={openBooking} />

      {/* The Solution Section - NEW INTERACTIVE */}
      <YourPlatformSection openBooking={openBooking} />

      {/* Platform Modules Section - NEW INTERACTIVE */}
      <CoreModulesSection features={features} openBooking={openBooking} />

      {/* AI Differentiator Section */}
      <section className="py-20 md:py-28 bg-primary-navy text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-care-teal/20 rounded-full border border-care-teal/30 mb-6">
              <Brain className="text-care-teal mr-2" size={20} />
              <span className="text-sm font-medium">Powered by Advanced AI</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              AI That <span className="text-care-teal">Actually Helps</span> NDIS Providers
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Not just buzzwords—our AI engine detects risks early, identifies patterns, and keeps you ahead of NDIS compliance requirements
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { icon: Shield, title: 'Risk Detection', desc: 'Spot compliance risks before audits' },
              { icon: Activity, title: 'Incident Patterns', desc: 'Identify trends across your organisation' },
              { icon: TrendingUp, title: 'Workforce Planning', desc: 'Predict staffing needs in advance' },
              { icon: BarChart, title: 'Auto Reporting', desc: 'Generate NDIS-ready reports instantly' }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="bg-white/5 border-white/10 hover:bg-white/10 hover:border-care-teal/40 hover:-translate-y-2 hover:shadow-xl hover:shadow-care-teal/10 transition-all duration-300 group cursor-default">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 rounded-full bg-care-teal/20 flex items-center justify-center mx-auto mb-4 group-hover:bg-care-teal/30 group-hover:scale-110 transition-all duration-300">
                      <Icon className="text-care-teal" size={28} />
                    </div>
                    <h3 className="font-semibold text-lg mb-2 group-hover:text-care-teal transition-colors">{item.title}</h3>
                    <p className="text-white/70 group-hover:text-white/90 transition-colors">{item.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center">
            <p className="text-2xl font-semibold mb-6">
              This is <span className="text-care-teal">practical AI</span> — not marketing hype.
            </p>
            <Link to="/ai-features">
              <Button className="bg-care-teal hover:bg-care-teal/90 text-white px-8 py-6 text-lg group hover:shadow-lg hover:shadow-care-teal/30 transition-all">
                Explore AI Features
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Comparison Section - NEW INTERACTIVE */}
      <ComparisonSection />

      {/* Social Proof / Testimonials */}
      <section className="py-20 md:py-28 bg-page-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary-navy mb-6">
              Trusted by <span className="text-tech-blue">NDIS Providers</span> Across Australia
            </h2>
            <p className="text-xl text-text-secondary">Real results from real providers</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <Card key={testimonial.id || index} className="border border-border-line hover:border-tech-blue hover:-translate-y-2 transition-all duration-300 hover:shadow-xl bg-surface group">
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating || 5)].map((_, i) => (
                      <Star key={i} className="text-heritage-orange fill-heritage-orange group-hover:scale-110 transition-transform" size={20} style={{ transitionDelay: `${i * 50}ms` }} />
                    ))}
                  </div>
                  <p className="text-text-secondary mb-6 leading-relaxed italic group-hover:text-text-primary transition-colors">"{testimonial.text}"</p>
                  <div className="border-t border-border-line pt-4">
                    <div className="font-semibold text-text-primary">{testimonial.name}</div>
                    <div className="text-sm text-text-secondary">{testimonial.role}</div>
                    <div className="text-sm text-tech-blue">{testimonial.company}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Stats Row */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: '20K+', label: 'Participants Served' },
              { value: '99.8%', label: 'Compliance Rate' },
              { value: '60%', label: 'Admin Time Saved' },
              { value: '2-6 Weeks', label: 'Deployment Time' }
            ].map((stat, index) => (
              <div 
                key={index} 
                className="text-center p-6 bg-surface rounded-2xl border border-border-line shadow-sm hover:border-tech-blue/50 hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-default group"
              >
                <div className="text-3xl md:text-4xl font-bold text-tech-blue mb-2 group-hover:scale-110 transition-transform">{stat.value}</div>
                <div className="text-sm text-text-secondary group-hover:text-text-primary transition-colors">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-20 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-navy mb-6">
              Explore <span className="text-tech-blue">GetCare</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'NDIS Solutions', desc: 'Custom platforms for providers, SIL, support coordinators', link: '/solutions', icon: Users },
              { title: 'AI Features', desc: 'Intelligent compliance & automation', link: '/ai-features', icon: Brain },
              { title: 'Pricing', desc: 'Transparent build + hosting model', link: '/pricing', icon: Calculator },
              { title: 'About Us', desc: 'Our story, team & partnerships', link: '/about', icon: Award }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <Link key={index} to={item.link}>
                  <Card className="border border-border-line hover:border-tech-blue hover:-translate-y-2 transition-all duration-300 hover:shadow-xl h-full cursor-pointer group bg-surface">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 rounded-xl bg-tech-blue/10 flex items-center justify-center mb-4 group-hover:bg-tech-blue group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                        <Icon className="text-tech-blue group-hover:text-white transition-colors" size={24} />
                      </div>
                      <h3 className="font-bold text-text-primary mb-2 group-hover:text-tech-blue transition-colors">{item.title}</h3>
                      <p className="text-text-secondary text-sm group-hover:text-text-primary transition-colors">{item.desc}</p>
                      <div className="mt-4 flex items-center text-tech-blue text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                        Learn more <ArrowRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Demo Booking Section */}
      <section id="demo" className="py-20 md:py-28 bg-primary-navy text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Ready to <span className="text-care-teal">Own Your Software?</span>
            </h2>
            <p className="text-xl text-white/80">
              Book a free strategy call. We'll create a custom platform blueprint for your NDIS organisation.
            </p>
          </div>

          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-8 md:p-12">
              <form onSubmit={handleDemoSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-white">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={demoForm.name}
                      onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-care-teal focus:border-care-teal transition-all"
                      placeholder="John Smith"
                      data-testid="home-demo-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-white">Email *</label>
                    <input
                      type="email"
                      required
                      value={demoForm.email}
                      onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-care-teal focus:border-care-teal transition-all"
                      placeholder="john@example.com"
                      data-testid="home-demo-email"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-white">Phone *</label>
                    <input
                      type="tel"
                      required
                      value={demoForm.phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      className={`w-full px-4 py-3 rounded-lg bg-white/10 border text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-care-teal transition-all ${phoneError ? 'border-red-400' : 'border-white/20 focus:border-care-teal'}`}
                      placeholder="04XX XXX XXX"
                      data-testid="home-demo-phone"
                    />
                    {phoneError && (
                      <p className="text-red-400 text-xs mt-1">{phoneError}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-white">Organisation *</label>
                    <input
                      type="text"
                      required
                      value={demoForm.company}
                      onChange={(e) => setDemoForm({ ...demoForm, company: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-care-teal focus:border-care-teal transition-all"
                      placeholder="Your Organisation"
                      data-testid="home-demo-company"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-white">Organisation Website</label>
                  <input
                    type="url"
                    value={demoForm.website}
                    onChange={(e) => setDemoForm({ ...demoForm, website: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-care-teal focus:border-care-teal transition-all"
                    placeholder="https://www.yourcompany.com.au"
                    data-testid="home-demo-website"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-white">Staff Size</label>
                  <select
                    value={demoForm.staffSize}
                    onChange={(e) => setDemoForm({ ...demoForm, staffSize: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-care-teal focus:border-care-teal transition-all"
                    data-testid="home-demo-staff"
                  >
                    <option value="" className="bg-primary-navy text-white">Select staff size (optional)</option>
                    <option value="5-15" className="bg-primary-navy text-white">5-15 Staff</option>
                    <option value="15-50" className="bg-primary-navy text-white">15-50 Staff</option>
                    <option value="50+" className="bg-primary-navy text-white">50+ Staff</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-white">What challenges are you facing?</label>
                  <textarea
                    value={demoForm.message}
                    onChange={(e) => setDemoForm({ ...demoForm, message: e.target.value })}
                    rows="4"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-care-teal focus:border-care-teal transition-all resize-none"
                    placeholder="Tell us about your current software frustrations and what you're looking for..."
                    data-testid="home-demo-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting || phoneError}
                  className="w-full bg-tech-blue hover:bg-[#1558C9] text-white py-6 text-lg h-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="home-demo-submit"
                >
                  {isSubmitting ? 'Booking...' : 'Book Free Strategy Call'}
                </Button>

                <p className="text-center text-white/60 text-sm">
                  No obligation. We'll discuss your needs and show you what's possible.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
